import DOMPurify from 'dompurify';
import get from 'lodash/get';
import { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { ConsentGuard } from '@hivebrite/design-system/Embed';
import { linkEffect } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { extractConsentGuardProps } from 'hb-react/shared/components/HtmlContent/utils/extractConsentGuardProps';
import { replaceIframesWithSpans } from 'hb-react/shared/components/HtmlContent/utils/replaceIframesWithSpans';
import { listStyles } from 'hb-react/shared/constants/styles';

import ErrorBoundary from '../ErrorBoundary';

interface HtmlContentProps {
  content: string;
  // eslint-disable-next-line react/no-unused-prop-types
  config?: DOMPurify.Config;
  withEditorStyles?: boolean;
}

const sanitizeHtml = (html: string, config: DOMPurify.Config | undefined) =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['form'],
    ...(config ?? {}),
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
  });

export const HtmlContent = ({
  content,
  config,
  withEditorStyles,
}: HtmlContentProps) => {
  const HtmlDiv = withEditorStyles ? DivWithEditorStyles : 'div';

  const { currentNetwork } = useDefaultAppContexts();
  const containerRef = useRef<HTMLDivElement>(null);

  const [portals, setPortals] = useState<
    {
      element: JSX.Element;
      target: Element;
      key: string;
    }[]
  >([]);

  const displayThirdPartyConsent = useMemo(
    () =>
      // Do not use destructuring. In some cases BO.currentNetwork
      // may be undefined for awhile.
      get(
        currentNetwork,
        'features.cookies.config.display_third_party_consent',
      ),
    [currentNetwork],
  );

  const { dangerouslySetInnerHTML, replacements } = useMemo(() => {
    const sanitizedHtml = sanitizeHtml(content, config);

    if (!displayThirdPartyConsent) {
      return {
        dangerouslySetInnerHTML: { __html: sanitizedHtml },
        replacements: [],
      };
    }

    const { replacements, modifiedHtml } =
      replaceIframesWithSpans(sanitizedHtml);

    return {
      dangerouslySetInnerHTML: { __html: modifiedHtml },
      replacements,
    };
  }, [config, content, displayThirdPartyConsent]);

  // We use a combination of useEffect (which runs after the
  // render is committed to the screen) and a ref (to get a stable
  // reference to the container for imperative DOM manipulation) to ensure
  // we can reliably retrieve the portals targets even if they are rendered
  // within this component.
  useEffect(() => {
    // TS has a hard time understanding containerRef.current is not null
    // if we access it directly. So we need to assign it to a variable first.
    const container = containerRef.current;

    if (container) {
      const newPortals = replacements
        ?.map(({ iframe, spanId }) => {
          const span = container.querySelector(`#${spanId}`);

          if (!span) {
            return null;
          }

          const { hostName, providerName, aspectRatio } =
            extractConsentGuardProps(iframe);

          return {
            element: (
              <ConsentGuard
                hostName={hostName}
                providerName={providerName}
                aspectRatio={aspectRatio}
                isActive
              >
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: iframe.outerHTML }} />
              </ConsentGuard>
            ),
            target: span,
            key: spanId,
          };
        })
        .filter(Boolean);

      setPortals(newPortals);
    }
  }, [replacements]);

  return (
    <ErrorBoundary>
      <HtmlDiv
        ref={containerRef}
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
      {portals.map(({ element, target, key }) =>
        createPortal(element, target, key),
      )}
    </ErrorBoundary>
  );
};

const withIframeConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
};

export const HtmlContentWithIframe = ({
  content,
  withEditorStyles,
}: HtmlContentProps) => (
  <HtmlContent
    content={content}
    config={withIframeConfig}
    withEditorStyles={withEditorStyles}
  />
);

// Required for app/assets/javascripts/initializers/react/components/shared.js
export default HtmlContent;

// Most of the styles in here are from app/assets/legacy/stylesheets/components/ckeditor_content.scss
const DivWithEditorStyles = styled.div`
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  hr {
    border-color: #ccc;
  }

  ${listStyles}

  a:not(.button) {
    color: ${({ theme }) => theme.colors.link};
  }

  a:not(.button),
  a:not(.button) span {
    ${linkEffect()}
  }

  figure {
    text-align: center;
    padding: 10px;
    margin: 10px 20px;
    display: inline-block;
  }
  figcaption {
    padding-top: 5px;
    text-align: center;
  }
  p {
    line-height: 1.6;
  }
  img {
    max-width: 100%;
  }
  .img-center {
    text-align: center;
  }
  .img-left {
    float: left;
  }

  .img-right {
    float: right;
  }
  strong,
  b {
    font-weight: bold;
  }
  i,
  cite,
  em,
  var,
  address,
  dfn {
    font-style: italic;
  }
  u,
  ins {
    text-decoration: underline;
  }
  s,
  strike,
  del {
    text-decoration: line-through;
  }
  sub {
    vertical-align: sub;
  }
  sup {
    vertical-align: super;
  }
  sub,
  sup {
    font-size: smaller;
  }
  tt,
  code,
  kbd,
  samp {
    font-family: monospace;
  }
  blockquote {
    display: block;
    font-style: italic;
    font-family: Georgia, Times, 'Times New Roman', serif;
    padding: 2px 0;
    border-style: solid;
    border-color: #ccc;
    border-width: 0;
    margin: 1em 40px;
    padding-left: 20px;
    padding-right: 8px;
    border-left-width: 5px;
  }
  q {
    quotes: initial;
  }
  q::before {
    content: open-quote;
  }
  q::after {
    content: close-quote;
  }
  big {
    font-size: larger;
  }
  small {
    font-size: smaller;
  }
  .marker {
    background-color: Yellow;
  }

  .embedly-embed {
    max-width: 100%;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 400;
    color: #2f363b;
    text-rendering: optimizeLegibility !important;
    -webkit-font-smoothing: subpixel-antialiased !important;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h1 {
    font-size: 2.1rem;
    margin-bottom: 2.1rem;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h2 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h3 {
    font-size: 1.4rem;
    margin-bottom: 1.4rem;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h4 {
    font-size: 1.3rem;
    margin-bottom: 1.3rem;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h5 {
    font-size: 1.2rem;
    margin-bottom: 1%.2;
  }

  // Styles from app/assets/legacy/stylesheets/base/headings.scss
  h6 {
    font-size: 1.1rem;
    margin-bottom: 1.1rem;
  }

  table {
    border-collapse: collapse;
    font-size: 1em;
    width: 100%;

    td,
    th {
      vertical-align: top;
    }

    thead td,
    th {
      font-weight: bold;
    }

    &[border] {
      td,
      th,
      thead td {
        border: 1px solid currentColor;
      }
    }

    &[border='0'] {
      &,
      td,
      th {
        border: none;
      }
    }
  }
`;
